import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { CustomFieldControl, DataService, SharedModule, NotificationService, FormInputComponent } from '@vendure/admin-ui/core';
import { Observable, Subscription } from 'rxjs';
import { take, filter, switchMap, shareReplay } from 'rxjs/operators';
import { GET_PRODUCT_REVIEW, GET_REVIEWS_FOR_PRODUCT } from '../graphql';
import { Product, ProductReview, Query } from '../generated';

@Component({
    selector: 'product-review-selector',
    template: `
        <div class="flex flex-row items-center">
            <div class="flex flex-col">
                <div>
                    <vdr-dynamic-form-input
                        [control]="productControl"
                        [def]="productConfig"
                    ></vdr-dynamic-form-input>
                </div>
                <div *ngIf="productReviews$ | async as reviews">
                    <select [formControl]="formControl">
                        <option [ngValue]="null" disabled>Select a review...</option>
                        <option *ngFor="let review of reviews" [ngValue]="review">
                            {{ review.summary }} <strong>{{ review.rating }}/5</strong>
                        </option>
                    </select>
                </div>
            </div>
            <div *ngIf="selectedProductReview" class="mt-2 cursor-pointer">
                <a [routerLink]="['/extensions', 'product-reviews', selectedProductReview.id]">
                    <span>Selected Review</span><br>
                    <span><strong>{{ selectedProductReview.rating }}/5 </strong> {{ selectedProductReview.summary }}</span><br>
                    <span>Product: {{ selectedProduct?.name }}</span>
                </a>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [SharedModule],
})
export class ProductReviewSelectorComponent implements OnInit, OnDestroy, CustomFieldControl {
    @Input() readonly: boolean;
    @Input() formControl: FormControl;
    @Input() config: any;

    productControl = new UntypedFormControl();
    productReviews$: Observable<ProductReview[] | undefined>;
    allProductReviews: ProductReview[];

    productConfig = {
        type: 'relation',
        list: false,
        entity: 'Product',
        ui: {
            component: 'relation-form-input',
        },
        label: 'Select a Product for reviews',
        defaultValue: null,
    };
    selectedProduct: Product | null = null;
    selectedProductReview: ProductReview | null = null;

    private subscriptions: Subscription[] = [];

    constructor(
        private dataService: DataService,
        private changeDetector: ChangeDetectorRef,
        private notificationService: NotificationService
    ) {}

    async ngOnInit() {
        // Subscribe to productControl.valueChanges
        const productControlSub = this.productControl.valueChanges
            .pipe(
                filter(product => !!product),
                switchMap(product => {
                    this.selectedProduct = product;
                    return this.loadProductReviews(product.id);
                })
            )
            .subscribe();
        this.subscriptions.push(productControlSub);
        // Subscribe to formControl.valueChanges
        const formControlSub = this.formControl.valueChanges.subscribe(review => {
            if (review) {
                this.selectedProductReview = review;
                this.selectedProduct = review.product;
                this.formControl.markAsDirty();
                this.changeDetector.markForCheck();
            } else {
                this.selectedProductReview = null;
                this.changeDetector.markForCheck();
            }
        });
        this.subscriptions.push(formControlSub);

        if (this.formControl.value) {
            const productReview = this.formControl.value;
            this.selectedProductReview = productReview;
            this.selectedProduct = productReview.product;
            if(!productReview.product) {
                const productReviewWithProduct = await this.fetchProductReview(productReview.id);
                if (productReviewWithProduct) {
                    this.selectedProduct = productReviewWithProduct.product;
                }
            }
            this.productControl.setValue(this.selectedProduct);
            this.changeDetector.markForCheck();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private async fetchProductReview(productReviewId: string): Promise<any> {
        try {
          const productReviewFetcher$ = this.dataService
            .query<{ productReview: ProductReview }>(
              GET_PRODUCT_REVIEW,
              { productReviewId: productReviewId }
            )
            .mapSingle((data: any) => data.productReview)
            .pipe(take(1));
          const data = await productReviewFetcher$.toPromise();
          return data;
        } catch (error) {
          console.error('Error fetching product review:', error);
          this.notificationService.error(`Error fetching product review ${productReviewId}: ${error}`);
          return null;
        }
    }

    private loadProductReviews(productId: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const source$ = this.dataService
                .query<Query['productReviews']>(GET_REVIEWS_FOR_PRODUCT, { productId })
                .mapSingle((data: any)=> data?.product?.reviews.items)
                .pipe(shareReplay(1));
            this.productReviews$ = source$;
            source$.subscribe(
                values => {
                    if (values) {
                        this.allProductReviews = values;
                        this.changeDetector.markForCheck();
                        resolve();
                    } else {
                        reject('No Product Reviews found');
                    }
                },
                error => {
                    this.notificationService.error('Failed to load product reviews');
                    reject(error);
                }
            );
        });
    }
}

